import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Globe, 
  Briefcase, 
  Users, 
  Lightbulb,
  ExternalLink,
  CheckCircle
} from 'lucide-react';
import { companyInformationSchema, CompanyInformationFormData } from '../../../schemas/onboarding';
import { INDUSTRIES, COMPANY_SIZES } from '../../../constants/onboarding';
import { MultiSelect } from '../MultiSelect';

interface CompanyStepProps {
  data: Partial<CompanyInformationFormData>;
  onNext: (data: CompanyInformationFormData) => void;
  onBack: () => void;
  className?: string;
}

export const CompanyStep: React.FC<CompanyStepProps> = ({
  data,
  onNext,
  onBack,
  className = '',
}) => {
  const [isValidatingWebsite, setIsValidatingWebsite] = useState(false);
  const [websiteValid, setWebsiteValid] = useState<boolean | null>(null);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<CompanyInformationFormData>({
    resolver: zodResolver(companyInformationSchema),
    defaultValues: data,
    mode: 'onChange',
  });

  const watchedWebsite = watch('website');
  const watchedCompanyName = watch('companyName');

  // Auto-validate website
  React.useEffect(() => {
    if (watchedWebsite && watchedWebsite.length > 5) {
      setIsValidatingWebsite(true);
      const timer = setTimeout(() => {
        try {
          new URL(watchedWebsite.startsWith('http') ? watchedWebsite : `https://${watchedWebsite}`);
          setWebsiteValid(true);
        } catch {
          setWebsiteValid(false);
        }
        setIsValidatingWebsite(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [watchedWebsite]);

  const onSubmit = (formData: CompanyInformationFormData) => {
    onNext(formData);
  };

  const industryOptions = INDUSTRIES.map(industry => ({
    value: industry,
    label: industry,
  }));

  const companySizeOptions = COMPANY_SIZES.map(size => ({
    value: size,
    label: size,
  }));

  // Auto-suggestions based on company name
  const getIndustrySuggestions = () => {
    if (!watchedCompanyName) return [];
    
    const name = watchedCompanyName.toLowerCase();
    const suggestions = [];
    
    if (name.includes('tech') || name.includes('software') || name.includes('app')) {
      suggestions.push('Technology');
    }
    if (name.includes('health') || name.includes('medical') || name.includes('care')) {
      suggestions.push('Healthcare');
    }
    if (name.includes('finance') || name.includes('bank') || name.includes('pay')) {
      suggestions.push('Financial Services');
    }
    if (name.includes('market') || name.includes('agency') || name.includes('media')) {
      suggestions.push('Marketing & Advertising');
    }
    
    return suggestions;
  };

  return (
    <div className={`max-w-2xl mx-auto px-6 py-8 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex p-3 bg-primary-50 rounded-full mb-4">
            <Building2 className="w-6 h-6 text-primary-500" />
          </div>
          <h2 className="text-3xl font-bold text-neutral-900 mb-2">
            Tell us about your company
          </h2>
          <p className="text-neutral-600">
            Help us understand your business so we can create the perfect outreach strategy
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Name */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Company Name *
            </label>
            <div className="relative">
              <input
                {...register('companyName')}
                type="text"
                placeholder="Acme Corporation"
                className={`
                  w-full px-4 py-3 border rounded-xl transition-all duration-200 ease-out
                  bg-neutral-50 focus:bg-white focus:ring-4 focus:ring-primary-500/10
                  ${errors.companyName 
                    ? 'border-error-500 focus:border-error-500' 
                    : 'border-neutral-300 focus:border-primary-500'
                  }
                `}
              />
              <Building2 className="absolute right-3 top-3.5 w-5 h-5 text-neutral-400" />
            </div>
            {errors.companyName && (
              <p className="text-sm text-error-600 mt-1">{errors.companyName.message}</p>
            )}
          </motion.div>

          {/* Website */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Website (Optional)
            </label>
            <div className="relative">
              <input
                {...register('website')}
                type="text"
                placeholder="https://acmecorp.com"
                className={`
                  w-full px-4 py-3 border rounded-xl transition-all duration-200 ease-out
                  bg-neutral-50 focus:bg-white focus:ring-4 focus:ring-primary-500/10
                  ${errors.website 
                    ? 'border-error-500 focus:border-error-500' 
                    : websiteValid === true
                      ? 'border-success-500 focus:border-success-500'
                      : 'border-neutral-300 focus:border-primary-500'
                  }
                `}
              />
              <div className="absolute right-3 top-3.5 flex items-center gap-2">
                {isValidatingWebsite && (
                  <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                )}
                {websiteValid === true && !isValidatingWebsite && (
                  <CheckCircle className="w-5 h-5 text-success-500" />
                )}
                <Globe className="w-5 h-5 text-neutral-400" />
              </div>
            </div>
            {errors.website && (
              <p className="text-sm text-error-600 mt-1">{errors.website.message}</p>
            )}
            {watchedWebsite && !errors.website && (
              <p className="text-sm text-neutral-500 mt-1 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                We'll use this to better understand your business
              </p>
            )}
          </motion.div>

          {/* Industry */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Industries *
            </label>
            <Controller
              name="industries"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  options={industryOptions}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Select your industries"
                  suggestions={getIndustrySuggestions()}
                  error={errors.industries?.message}
                />
              )}
            />
          </motion.div>

          {/* Company Size */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Company Size *
            </label>
            <Controller
              name="companySize"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  options={companySizeOptions}
                  value={field.value ? [field.value] : []}
                  onChange={(values) => field.onChange(values[0] || '')}
                  placeholder="Select your company size"
                  maxItems={1}
                  error={errors.companySize?.message}
                />
              )}
            />
          </motion.div>

          {/* Product Description */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Product/Service Description *
            </label>
            <textarea
              {...register('productDescription')}
              rows={4}
              placeholder="Describe what your company offers. Be specific about your main products or services..."
              className={`
                w-full px-4 py-3 border rounded-xl transition-all duration-200 ease-out resize-none
                bg-neutral-50 focus:bg-white focus:ring-4 focus:ring-primary-500/10
                ${errors.productDescription 
                  ? 'border-error-500 focus:border-error-500' 
                  : 'border-neutral-300 focus:border-primary-500'
                }
              `}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.productDescription ? (
                <p className="text-sm text-error-600">{errors.productDescription.message}</p>
              ) : (
                <p className="text-sm text-neutral-500 flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" />
                  Be specific - this helps our AI understand your offering
                </p>
              )}
              <span className="text-sm text-neutral-400">
                {watch('productDescription')?.length || 0}/500
              </span>
            </div>
          </motion.div>

          {/* Value Proposition */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Value Proposition *
            </label>
            <textarea
              {...register('valueProposition')}
              rows={3}
              placeholder="What makes your product/service unique? How do you help customers?"
              className={`
                w-full px-4 py-3 border rounded-xl transition-all duration-200 ease-out resize-none
                bg-neutral-50 focus:bg-white focus:ring-4 focus:ring-primary-500/10
                ${errors.valueProposition 
                  ? 'border-error-500 focus:border-error-500' 
                  : 'border-neutral-300 focus:border-primary-500'
                }
              `}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.valueProposition ? (
                <p className="text-sm text-error-600">{errors.valueProposition.message}</p>
              ) : (
                <p className="text-sm text-neutral-500">
                  Focus on the benefits and outcomes you deliver
                </p>
              )}
              <span className="text-sm text-neutral-400">
                {watch('valueProposition')?.length || 0}/300
              </span>
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="flex items-center justify-between pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 text-neutral-600 hover:text-neutral-900 font-medium transition-colors"
            >
              Back
            </button>
            
            <button
              type="submit"
              disabled={!isValid}
              className={`
                px-8 py-3 rounded-xl font-semibold transition-all duration-200
                ${isValid
                  ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-glow-primary hover:shadow-strong'
                  : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                }
              `}
            >
              Continue to Targeting
            </button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}; 